import type { ThreeEvent } from "@react-three/fiber";
import { useEffect, useRef } from "react";
import * as THREE from "three/webgpu";

const WIRE_EDGE_HIT_RADIUS = 0.06;
const WIRE_EDGE_HIT_GEOMETRY = new THREE.CylinderGeometry(
  WIRE_EDGE_HIT_RADIUS,
  WIRE_EDGE_HIT_RADIUS,
  1,
  6,
);
const WIRE_EDGE_HIT_UP = new THREE.Vector3(0, 1, 0);

export function WireEdgeHitMesh({
  vertices,
  v0,
  v1,
  name,
  userData,
  onPointerDown,
  onPointerUp,
  onPointerMove,
}: {
  vertices: Float32Array;
  v0: number;
  v1: number;
  name?: string;
  userData?: Record<string, unknown>;
  onPointerDown?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerUp?: (e: ThreeEvent<PointerEvent>) => void;
  onPointerMove?: (e: ThreeEvent<PointerEvent>) => void;
}) {
  const meshRef = useRef<THREE.Mesh>(null);

  useEffect(() => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const ax = vertices[v0 * 3];
    const ay = vertices[v0 * 3 + 1];
    const az = vertices[v0 * 3 + 2];
    const bx = vertices[v1 * 3];
    const by = vertices[v1 * 3 + 1];
    const bz = vertices[v1 * 3 + 2];
    mesh.position.set((ax + bx) * 0.5, (ay + by) * 0.5, (az + bz) * 0.5);

    const dx = bx - ax;
    const dy = by - ay;
    const dz = bz - az;
    const length = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (length < 1e-6) return;

    const dir = new THREE.Vector3(dx / length, dy / length, dz / length);
    mesh.quaternion.setFromUnitVectors(WIRE_EDGE_HIT_UP, dir);
    mesh.scale.set(1, length, 1);
  }, [vertices, v0, v1]);

  return (
    <mesh
      ref={meshRef}
      name={name}
      geometry={WIRE_EDGE_HIT_GEOMETRY}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerMove={onPointerMove}
      userData={userData}
    >
      <meshBasicMaterial visible={false} side={THREE.DoubleSide} />
    </mesh>
  );
}
